import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import eventosGanttService from '../services/eventosGanttService';
import './TareaEventoTimeModal.css';

import Loading from './Loading';
/**
 * Modal para visualizar y agregar registros de tiempo a tareas de evento
 */
const TareaEventoTimeModal = ({ isOpen, onClose, tarea, onTimeLogAdded }) => {
  const [timeLogs, setTimeLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recursosDisponibles, setRecursosDisponibles] = useState([]);

  const [manualTimeForm, setManualTimeForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    minutos: '',
    notas: '',
    presupuesto_costo_recursos: []
  });

  // Cargar timelogs y recursos cuando se abre el modal
  useEffect(() => {
    if (isOpen && tarea) {
      cargarTimeLogs();
      cargarRecursosDeTarea();
    }
  }, [isOpen, tarea]);

  const cargarRecursosDeTarea = async () => {
    if (!tarea || !tarea.id) return;
    try {
      console.log('🔍 [TimeModal] Cargando recursos para tarea:', tarea.id);

      // Obtener el detalle de la tarea para acceder a sus recursos
      const response = await eventosGanttService.tareas.retrieve(tarea.id);
      const tareaDetalle = response.data;
      console.log('📋 [TimeModal] Tarea detalle:', tareaDetalle);
      console.log('📦 [TimeModal] Recursos detalle:', tareaDetalle.presupuesto_costo_recursos_detalle);

      // Si la tarea tiene recursos asociados, filtrarlos
      if (tareaDetalle.presupuesto_costo_recursos_detalle && tareaDetalle.presupuesto_costo_recursos_detalle.length > 0) {
        // Filtrar solo los recursos que sean de tipo "Mano de obra"
        const recursosManoDeObra = tareaDetalle.presupuesto_costo_recursos_detalle.filter(r => {
          const tipoRecurso = r.recurso_referencia_data?.tipo_recurso?.nombre;
          const esManoDeObra = tipoRecurso === 'Mano de obra';

          console.log(`   [TimeModal] Recurso "${r.nombre}":`, {
            id: r.id,
            tipoRecurso,
            esManoDeObra
          });

          return esManoDeObra;
        });

        console.log('✅ [TimeModal] Recursos filtrados:', recursosManoDeObra);
        setRecursosDisponibles(recursosManoDeObra);
      } else {
        console.log('⚠️ [TimeModal] La tarea no tiene recursos asociados');
        setRecursosDisponibles([]);
      }
    } catch (error) {
      console.error('❌ [TimeModal] Error al cargar recursos de la tarea:', error);
      setRecursosDisponibles([]);
    }
  };

  const cargarTimeLogs = async () => {
    if (!tarea) return;
    try {
      setLoadingLogs(true);
      const response = await eventosGanttService.timelogs.listByTarea(tarea.id);
      const logs = Array.isArray(response.data) ? response.data : (response.data.results || response.data || []);
      setTimeLogs(logs);
    } catch (error) {
      console.error('Error al cargar timelogs:', error);
      toast.error('Error al cargar registros de tiempo');
    } finally {
      setLoadingLogs(false);
    }
  };

  const validateManualForm = () => {
    if (!manualTimeForm.fecha.trim()) {
      toast.error('La fecha es requerida');
      return false;
    }

    if (!manualTimeForm.minutos || parseFloat(manualTimeForm.minutos) <= 0) {
      toast.error('Por favor ingresa un tiempo válido en minutos (mayor a 0)');
      return false;
    }

    return true;
  };

  const handleAddManualTime = async () => {
    if (!validateManualForm()) return;

    setLoading(true);
    try {
      const timelogData = {
        tarea: tarea.id,
        fecha: manualTimeForm.fecha,
        minutos: parseInt(manualTimeForm.minutos),
        notas: manualTimeForm.notas,
        presupuesto_costo_recursos: manualTimeForm.presupuesto_costo_recursos
      };

      const response = await eventosGanttService.timelogs.create(timelogData);
      console.log('✅ [TimeModal] TimeLog creado exitosamente');

      // Recalcular horas de la tarea
      await eventosGanttService.tareas.recalcularHoras(tarea.id);
      console.log('✅ [TimeModal] Horas recalculadas para la tarea:', tarea.id);

      // Pequeño delay para asegurar que el backend procese la actualización
      await new Promise(resolve => setTimeout(resolve, 300));

      // Recargar timelogs y limpiar formulario
      await cargarTimeLogs();
      setManualTimeForm({
        fecha: new Date().toISOString().split('T')[0],
        minutos: '',
        notas: '',
        presupuesto_costo_recursos: []
      });
      setShowManualForm(false);

      // Disparar evento global para notificar cambios y actualizar el badge
      window.dispatchEvent(new CustomEvent('tareaTimeLogAdded', { detail: { tareaId: tarea.id } }));
      console.log('✅ [TimeModal] Evento tareaTimeLogAdded disparado');

      // Notificar al componente padre para actualizar el badge de tiempo total
      if (onTimeLogAdded) {
        onTimeLogAdded();
      }

      toast.success('Tiempo registrado correctamente');
    } catch (error) {
      console.error('Error al agregar tiempo:', error);
      const errorMsg = error.response?.data?.detail ||
                      Object.values(error.response?.data || {}).flat().join(', ') ||
                      'Error al agregar tiempo';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeLog = async (timelogId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) return;

    try {
      await eventosGanttService.timelogs.delete(timelogId);
      console.log('✅ [TimeModal] TimeLog eliminado exitosamente');

      // Recalcular horas de la tarea
      await eventosGanttService.tareas.recalcularHoras(tarea.id);
      console.log('✅ [TimeModal] Horas recalculadas después de eliminar');

      // Pequeño delay para asegurar que el backend procese la actualización
      await new Promise(resolve => setTimeout(resolve, 300));

      // Recargar timelogs
      await cargarTimeLogs();

      // Disparar evento global para notificar cambios y actualizar el badge
      window.dispatchEvent(new CustomEvent('tareaTimeLogAdded', { detail: { tareaId: tarea.id } }));
      console.log('✅ [TimeModal] Evento disparado después de eliminar');

      // Notificar al componente padre para actualizar el badge de tiempo total
      if (onTimeLogAdded) {
        onTimeLogAdded();
      }

      toast.success('Registro eliminado correctamente');
    } catch (error) {
      console.error('Error al eliminar timelog:', error);
      toast.error('Error al eliminar registro');
    }
  };

  const formatDuration = (horaInicio, horaFin) => {
    try {
      const inicio = new Date(`2000-01-01T${horaInicio}`);
      const fin = new Date(`2000-01-01T${horaFin}`);

      let diferencia = (fin - inicio) / (1000 * 60 * 60); // Convertir a horas

      // Si fin es anterior a inicio, asumir que es al día siguiente
      if (diferencia < 0) {
        diferencia += 24;
      }

      const horas = Math.floor(diferencia);
      const minutos = Math.round((diferencia - horas) * 60);

      return `${horas}h ${minutos}m`;
    } catch (error) {
      return 'N/A';
    }
  };

  const getTotalTime = () => {
    if (!timeLogs || timeLogs.length === 0) return 0;
    // Convertir segundos a horas
    const total = timeLogs.reduce((sum, log) => sum + ((log.duration_seconds || 0) / 3600), 0);
    return total.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (!isOpen || !tarea) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content tarea-evento-time-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>⏱️ Registros de Tiempo - {tarea.titulo}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {/* Resumen de tiempo total */}
          <div className="time-summary">
            <div className="time-total-box">
              <div className="time-label">Tiempo Total Registrado</div>
              <div className="time-value">{getTotalTime()} horas</div>
              <div className="time-subtitle">{timeLogs.length} registros</div>
            </div>
          </div>

          {/* Tabla de timelogs */}
          {loadingLogs ? (
            <Loading fullScreen={false} size="sm" mensaje="Cargando registros" />
          ) : timeLogs && timeLogs.length > 0 ? (
            <div className="timelogs-table">
              <div className="table-header">
                <div className="col col-fecha">Fecha Inicio</div>
                <div className="col col-usuario">Usuario</div>
                <div className="col col-horas">Hora Inicio</div>
                <div className="col col-horas">Hora Fin</div>
                <div className="col col-duracion">Duración</div>
                <div className="col col-recursos">Recursos</div>
                <div className="col col-notas">Notas</div>
                <div className="col col-acciones">Acciones</div>
              </div>
              <div className="table-body">
                {timeLogs.map(log => (
                  <div key={log.id} className="table-row">
                    <div className="col col-fecha">
                      {formatDate(log.started_at)}
                    </div>
                    <div className="col col-usuario">
                      {log.user_name || 'N/A'}
                    </div>
                    <div className="col col-horas">
                      {log.started_at ? new Date(log.started_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                    </div>
                    <div className="col col-horas">
                      {log.ended_at ? new Date(log.ended_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                    </div>
                    <div className="col col-duracion">
                      <span className="duration-badge">
                        {log.formatted_duration || 'N/A'}
                      </span>
                    </div>
                    <div className="col col-recursos">
                      {log.recursos_detalle && log.recursos_detalle.length > 0 ? (
                        <div style={{ fontSize: '0.85em' }}>
                          {log.recursos_detalle.map(r => (
                            <div key={r.id} title={`$${parseFloat(r.precio_unitario_costo).toLocaleString('es-CL')}`}>
                              • {r.nombre}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </div>
                    <div className="col col-notas">
                      <span className="notes-text" title={log.notes}>
                        {log.notes || '-'}
                      </span>
                    </div>
                    <div className="col col-acciones">
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteTimeLog(log.id)}
                        title="Eliminar registro"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p>No hay registros de tiempo yet</p>
            </div>
          )}

          {/* Formulario manual */}
          {!showManualForm ? (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={() => setShowManualForm(true)}
              >
                + Agregar Tiempo Manual
              </button>
            </div>
          ) : (
            <div className="manual-time-form">
              <h3>Agregar Tiempo Manual</h3>

              <form onSubmit={(e) => {
                e.preventDefault();
                handleAddManualTime();
              }}>
                <div className="form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="form-group">
                    <label htmlFor="fecha">Fecha *</label>
                    <input
                      id="fecha"
                      type="date"
                      value={manualTimeForm.fecha}
                      onChange={(e) => setManualTimeForm({ ...manualTimeForm, fecha: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      required
                      style={{ boxSizing: 'border-box' }}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="minutos">Tiempo (minutos) *</label>
                    <input
                      id="minutos"
                      type="number"
                      min="1"
                      value={manualTimeForm.minutos}
                      onChange={(e) => setManualTimeForm({ ...manualTimeForm, minutos: e.target.value })}
                      placeholder="ej: 30"
                      required
                      style={{ boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="notas">Notas (opcional)</label>
                  <textarea
                    id="notas"
                    value={manualTimeForm.notas}
                    onChange={(e) => setManualTimeForm({ ...manualTimeForm, notas: e.target.value })}
                    placeholder="Describe en qué trabajaste..."
                    rows={3}
                    style={{ boxSizing: 'border-box' }}
                  />
                </div>

                {/* Recursos del Presupuesto (multiselect) */}
                {recursosDisponibles.length > 0 && (
                  <div className="form-group">
                    <label htmlFor="presupuesto_costo_recursos">
                      Recursos Utilizados (opcional)
                      <span style={{ fontSize: '0.85em', color: '#6b7280', marginLeft: '8px' }}>
                        Mantén presionado Ctrl (Cmd en Mac) para seleccionar múltiples
                      </span>
                    </label>
                    <select
                      id="presupuesto_costo_recursos"
                      multiple
                      value={manualTimeForm.presupuesto_costo_recursos}
                      onChange={(e) => {
                        // Obtener todos los valores seleccionados
                        const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                        setManualTimeForm(prev => ({
                          ...prev,
                          presupuesto_costo_recursos: selectedOptions
                        }));
                      }}
                      style={{
                        boxSizing: 'border-box',
                        minHeight: '100px',
                        padding: '0.5rem'
                      }}
                    >
                      {recursosDisponibles.map(recurso => (
                        <option key={recurso.id} value={recurso.id}>
                          {recurso.nombre} - ${recurso.precio_unitario_costo?.toLocaleString('es-CL')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowManualForm(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Guardando...' : 'Guardar Tiempo'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TareaEventoTimeModal;
